import * as React from "react";
import { onError } from "src/common/onError";
import { ZSidLabel } from "src/types";

/**
 * Прокси-компонент для выбора сущностей по id через базовый компонент, который принимает SidLabel
 */

type PropsEditSidLabel = {
  value?: ZSidLabel;
  onChange?(newValue?: ZSidLabel): void;
};

type PropsSelectCachedId<BaseRestProps extends {}> = {
  value?: string;
  onChange?(newValue?: string): void;
  loadSidLabel(id: string): Promise<ZSidLabel>;
  cache: Record<string, string>;
  BaseComponent: React.FC<BaseRestProps & PropsEditSidLabel>;
  baseProps: BaseRestProps;
};

export const SelectCachedId = <BaseRestProps extends {}>(
  props: PropsSelectCachedId<BaseRestProps>,
): React.ReactNode => {
  const { value, onChange, loadSidLabel, cache, BaseComponent, baseProps } =
    props;
  const [extValue, setExtValue] = React.useState<ZSidLabel | undefined>();
  const load = async (entityId: string) => {
    const { id, label } = await loadSidLabel(entityId);
    cache[id] = label;
    setExtValue((prev) => (prev?.id === id ? { id, label } : undefined));
  };
  React.useEffect(() => {
    if (!value) {
      setExtValue(undefined);
    } else {
      const label = cache[value];
      if (label) {
        setExtValue({ id: value, label });
      } else {
        setExtValue({ id: value, label: "..." });
        load(value).catch(onError);
      }
    }
  }, [value]);
  const onChangeIn = (newValue?: ZSidLabel | null) => {
    if (newValue) {
      cache[newValue.id] = newValue.label;
    }
    onChange?.(newValue?.id);
  };
  return (
    <BaseComponent {...baseProps} value={extValue} onChange={onChangeIn} />
  );
};
